export const initialChatState = {
    byPeer: {},
    unreadByPeer: {},
    readUntilByPeer: {},
    historyMetaByPeer: {},
    activePeerId: null
};
function defaultHistoryMeta() {
    return {
        hasMoreOlder: false,
        loadingOlder: false,
        initialLoaded: false,
        initialLoading: false,
        initialError: null,
        oldestCursor: null
    };
}
export function peerIdForMessage(message, ownUserId) {
    return message.toId === ownUserId ? message.fromId : message.toId;
}
export function sortMessages(messages) {
    return [...messages].sort((a, b) => {
        const aSeq = Number.isFinite(Number(a.seq)) ? Number(a.seq) : null;
        const bSeq = Number.isFinite(Number(b.seq)) ? Number(b.seq) : null;
        if (aSeq !== null && bSeq !== null && aSeq !== bSeq)
            return aSeq - bSeq;
        if (a.ts !== b.ts)
            return a.ts - b.ts;
        return a.id.localeCompare(b.id);
    });
}
export function normalizeMessageFileInfo(input) {
    if (!input)
        return undefined;
    const raw = input;
    const id = raw.id ?? raw.fileId;
    const name = raw.name ?? raw.originalname ?? raw.filename;
    const mime = raw.mime ?? raw.type ?? raw.mimetype;
    const size = typeof raw.size === 'number' ? raw.size : Number(raw.size || 0);
    const url = raw.url ?? (id ? `/file/${String(id)}` : undefined);
    return {
        id: id ? String(id) : undefined,
        name: name ? String(name) : undefined,
        size: Number.isFinite(size) && size > 0 ? size : undefined,
        type: mime ? String(mime) : undefined,
        mime: mime ? String(mime) : undefined,
        url: url ? String(url) : undefined
    };
}
function upsert(list, message) {
    const idx = list.findIndex((item) => item.id === message.id);
    const next = idx >= 0
        ? [...list.slice(0, idx), { ...list[idx], ...message }, ...list.slice(idx + 1)]
        : [...list, message];
    return sortMessages(next);
}
function messageStatusRank(status) {
    switch (String(status || '').toLowerCase()) {
        case 'read': return 3;
        case 'delivered': return 2;
        case 'sent': return 1;
        case 'sending':
        case 'failed': return 0;
        default: return -1;
    }
}
function botMessageIsTerminal(message) {
    if (message.type !== 'bot_media' || !message.meta || typeof message.meta !== 'object' || Array.isArray(message.meta))
        return false;
    const phase = String(message.meta.phase || '').toLowerCase();
    return phase === 'complete' || phase === 'completed' || phase === 'done' || phase === 'failed' || phase === 'error';
}
export function mergeChatMessageState(current, hydrated) {
    const currentStatusRank = messageStatusRank(current.status);
    const hydratedStatusRank = messageStatusRank(hydrated.status);
    const status = hydratedStatusRank > currentStatusRank ? hydrated.status : current.status ?? hydrated.status;
    const readBy = [...new Set([...(hydrated.readBy || []), ...(current.readBy || [])])];
    const currentSeq = Number(current.seq || 0);
    const hydratedSeq = Number(hydrated.seq || 0);
    if (botMessageIsTerminal(hydrated) && !botMessageIsTerminal(current)) {
        const terminal = {
            ...current,
            ...hydrated,
            seq: currentSeq > 0 ? current.seq : hydratedSeq > 0 ? hydrated.seq : current.seq ?? hydrated.seq,
            readBy: readBy.length ? readBy : undefined
        };
        if (terminal.status !== 'failed')
            delete terminal.error;
        return terminal;
    }
    let next = {
        ...hydrated,
        ...current,
        seq: currentSeq > 0 ? current.seq : hydratedSeq > 0 ? hydrated.seq : current.seq ?? hydrated.seq,
        plain: current.plain ? current.plain : hydrated.plain,
        iv: current.iv || hydrated.iv,
        payload: current.payload ?? hydrated.payload,
        file: current.file || hydrated.file
            ? { ...(hydrated.file || {}), ...(current.file || {}) }
            : undefined,
        replyTo: current.replyTo || hydrated.replyTo
            ? { ...(hydrated.replyTo || { id: '' }), ...(current.replyTo || {}) }
            : undefined,
        status,
        readBy: readBy.length ? readBy : undefined
    };
    const currentDeleted = current.type === 'deleted';
    const hydratedDeleted = hydrated.type === 'deleted';
    if (currentDeleted || hydratedDeleted) {
        next = { ...next, type: 'deleted', plain: '', payload: null, file: undefined };
    }
    else {
        const currentEditedTs = Number(current.editedTs || 0);
        const hydratedEditedTs = Number(hydrated.editedTs || 0);
        if (hydrated.edited && (!current.edited || hydratedEditedTs > currentEditedTs)) {
            next = {
                ...next,
                type: hydrated.type,
                plain: hydrated.plain,
                iv: hydrated.iv,
                payload: hydrated.payload,
                file: hydrated.file,
                edited: true,
                editedTs: hydrated.editedTs
            };
        }
    }
    if (next.status !== 'failed')
        delete next.error;
    return next;
}
function upsertHydrated(list, message) {
    const idx = list.findIndex((item) => item.id === message.id);
    const next = idx >= 0
        ? [...list.slice(0, idx), mergeChatMessageState(list[idx], message), ...list.slice(idx + 1)]
        : [...list, message];
    return sortMessages(next);
}
export function chatReducer(state, action) {
    switch (action.type) {
        case 'reset':
            return initialChatState;
        case 'open-peer': {
            const existingMeta = state.historyMetaByPeer[action.peerId];
            const hasMessages = (state.byPeer[action.peerId] || []).length > 0;
            return {
                ...state,
                activePeerId: action.peerId,
                unreadByPeer: { ...state.unreadByPeer, [action.peerId]: 0 },
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(existingMeta || {}),
                        initialLoading: !existingMeta?.initialLoaded && !hasMessages,
                        initialError: null
                    }
                }
            };
        }
        case 'hydrate':
            return {
                ...state,
                byPeer: { ...state.byPeer, [action.peerId]: sortMessages(action.messages) },
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(state.historyMetaByPeer[action.peerId] || {})
                    }
                }
            };
        case 'merge-hydrate': {
            const current = state.byPeer[action.peerId] || [];
            const currentIds = new Set(current.map((message) => message.id));
            const unreadIds = new Set(action.unreadMessageIds || []);
            const unreadDelta = action.messages.reduce((count, message) => {
                if (!unreadIds.has(message.id) || currentIds.has(message.id))
                    return count;
                currentIds.add(message.id);
                return message.fromId && message.fromId !== action.ownUserId ? count + 1 : count;
            }, 0);
            const messages = action.messages.reduce((merged, message) => upsertHydrated(merged, message), current);
            const active = state.activePeerId === action.peerId;
            return {
                ...state,
                byPeer: { ...state.byPeer, [action.peerId]: messages },
                unreadByPeer: unreadDelta > 0 || active
                    ? {
                        ...state.unreadByPeer,
                        [action.peerId]: active ? 0 : (state.unreadByPeer[action.peerId] || 0) + unreadDelta
                    }
                    : state.unreadByPeer,
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(state.historyMetaByPeer[action.peerId] || {})
                    }
                }
            };
        }
        case 'prepend-history': {
            const current = state.byPeer[action.peerId] || [];
            return {
                ...state,
                byPeer: {
                    ...state.byPeer,
                    [action.peerId]: sortMessages([...action.messages, ...current])
                },
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(state.historyMetaByPeer[action.peerId] || {}),
                        loadingOlder: false,
                        initialLoaded: true,
                        initialLoading: false,
                        initialError: null,
                        hasMoreOlder: action.hasMoreOlder,
                        oldestCursor: action.oldestCursor
                    }
                }
            };
        }
        case 'set-history-loading':
            return {
                ...state,
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(state.historyMetaByPeer[action.peerId] || {}),
                        loadingOlder: action.loadingOlder
                    }
                }
            };
        case 'set-history-meta':
            return {
                ...state,
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: {
                        ...defaultHistoryMeta(),
                        ...(state.historyMetaByPeer[action.peerId] || {}),
                        ...action.patch
                    }
                }
            };
        case 'append': {
            const current = state.byPeer[action.peerId] || [];
            const existing = current.some((message) => message.id === action.message.id);
            const own = !!action.ownUserId && action.message.fromId === action.ownUserId;
            const active = state.activePeerId === action.peerId;
            const unread = own || active
                ? 0
                : (state.unreadByPeer[action.peerId] || 0) + (existing ? 0 : 1);
            return {
                ...state,
                byPeer: { ...state.byPeer, [action.peerId]: upsert(current, action.message) },
                unreadByPeer: { ...state.unreadByPeer, [action.peerId]: unread },
                historyMetaByPeer: {
                    ...state.historyMetaByPeer,
                    [action.peerId]: state.historyMetaByPeer[action.peerId] || defaultHistoryMeta()
                }
            };
        }
        case 'ack': {
            const byPeer = { ...state.byPeer };
            const peerIds = action.peerId ? [action.peerId] : Object.keys(byPeer);
            for (const peerId of peerIds) {
                const list = byPeer[peerId] || [];
                byPeer[peerId] = list.map((message) => {
                    if (message.id !== action.id)
                        return message;
                    const readBy = action.readBy || (action.readerId ? [...new Set([...(message.readBy || []), action.readerId])] : message.readBy);
                    return {
                        ...message,
                        status: action.status,
                        seq: action.seq ?? message.seq,
                        readBy,
                        error: action.error ?? (action.status === 'failed' ? message.error : undefined)
                    };
                });
            }
            return { ...state, byPeer };
        }
        case 'peer-read-until': {
            const list = state.byPeer[action.peerId] || [];
            return {
                ...state,
                byPeer: {
                    ...state.byPeer,
                    [action.peerId]: list.map((message) => {
                        if ((message.ts || 0) > action.readUntilTs)
                            return message;
                        const readBy = action.readerId
                            ? [...new Set([...(message.readBy || []), action.readerId])]
                            : message.readBy;
                        return {
                            ...message,
                            status: message.status === 'sending' || message.status === 'failed' ? message.status : 'read',
                            readBy
                        };
                    })
                },
                unreadByPeer: { ...state.unreadByPeer, [action.peerId]: 0 },
                readUntilByPeer: {
                    ...state.readUntilByPeer,
                    [action.peerId]: Math.max(state.readUntilByPeer[action.peerId] || 0, action.readUntilTs)
                }
            };
        }
        case 'edit':
            return {
                ...state,
                byPeer: {
                    ...state.byPeer,
                    [action.peerId]: (state.byPeer[action.peerId] || []).map((message) => message.id === action.id
                        ? {
                            ...message,
                            plain: action.plain ?? message.plain,
                            iv: action.iv ?? message.iv,
                            payload: action.payload ?? message.payload,
                            edited: true,
                            editedTs: action.editedTs ?? Date.now()
                        }
                        : message)
                }
            };
        case 'delete':
            return {
                ...state,
                byPeer: {
                    ...state.byPeer,
                    [action.peerId]: (state.byPeer[action.peerId] || []).map((message) => message.id === action.id
                        ? { ...message, type: 'deleted', plain: '', payload: null, file: undefined }
                        : message)
                }
            };
        case 'mark-read':
            return {
                ...state,
                unreadByPeer: { ...state.unreadByPeer, [action.peerId]: 0 }
            };
        case 'mark-unread':
            return {
                ...state,
                unreadByPeer: { ...state.unreadByPeer, [action.peerId]: Math.max(1, state.unreadByPeer[action.peerId] || 0) }
            };
        default:
            return state;
    }
}
//# sourceMappingURL=messages.js.map